import {css} from '@emotion/react';
import styled from '@emotion/styled';

// Design tokens as CSS custom properties
export const GlobalStyles = css`
  body {
    margin: 0;
    padding: 0;
    // overflow: hidden;
    background-color: var(--background);
  }

  :root {
    /* Colors */
    --background: hsl(0 0% 100%);
    --foreground: hsl(222.2 84% 4.9%);

    /* Toolbar heights */
    --toolbar-height: 4rem;
    --toolbar-height-tablet: 20rem;
    --toolbar-height-mobile: 20rem;

    --card: hsl(0 0% 100%);
    --card-foreground: hsl(222.2 84% 4.9%);

    --popover: hsl(0 0% 100%);
    --popover-foreground: hsl(222.2 84% 4.9%);

    // --primary: hsl(222.2 47.4% 11.2%);
    --primary-foreground: hsl(210 40% 98%);

    --secondary: hsl(210 40% 96.1%);
    --secondary-foreground: hsl(222.2 47.4% 11.2%);

    --muted: hsl(210 40% 96.1%);
    --muted-foreground: hsl(215.4 16.3% 46.9%);

    --accent: hsl(210 40% 96.1%);
    --accent-foreground: hsl(222.2 47.4% 11.2%);

    --destructive: hsl(0 84.2% 60.2%);
    --destructive-foreground: hsl(210 40% 98%);

    --input: hsl(214.3 31.8% 91.4%);
    --ring: hsl(222.2 84% 4.9%);

    --radius: 0.5rem;

    /* Shadows */
    --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);

    /* Font */
    --font-sans: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue',
      Arial, 'Noto Sans', sans-serif;
  }
`;

export const Container = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--background);
  padding-bottom: var(--toolbar-height);

  @media (max-width: 768px) {
    padding-bottom: var(--toolbar-height-tablet);
  }

  @media (max-width: 480px) {
    padding-bottom: var(--toolbar-height-mobile);
  }
`;

export const PreviewContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  display: flex;
  background-color: #000000;
  position: relative;
  z-index: 0;

  > div {
    width: 100%;
    height: 100%;
    display: flex;
  }

  .direction-row {
    width: 100%;
    height: 100%;
  }

  .flex-1 {
    flex: 1;
    height: 100%;
  }
`;

export const Toolbar = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  min-height: var(--toolbar-height);
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background-color: var(--card);
  border-top: 1px solid var(--border);
  z-index: 50;
  flex-wrap: wrap;
  justify-content: space-around;

  @media (max-width: 1024px) {
    gap: 0.75rem;
    padding: 0.5rem;
  }

  @media (max-width: 768px) {
    min-height: var(--toolbar-height-tablet);
    gap: 0.5rem;
    padding-bottom: env(safe-area-inset-bottom, 0.5rem);
  }

  @media (max-width: 480px) {
    min-height: var(--toolbar-height-mobile);
  }
`;

export const ToolbarSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 768px) {
    flex: 1 1 auto;
    min-width: 280px;
    justify-content: center;

    select,
    button {
      flex: 1;
      min-width: 120px;
    }
  }

  @media (max-width: 480px) {
    min-width: 100%;

    select,
    button {
      min-width: 0;
    }
  }
`;

export const ToolbarDivider = styled.div`
  width: 1px;
  height: 2rem;
  background-color: var(--border);
  margin: 0 0.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const LandingPageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 120px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--text);

  @media (max-width: 768px) {
    padding: 120px 3em;
  }
`;

export const CardContent = styled.div`
  color: var(--foreground);
  font-family: var(--font-sans);

  &.size-xs {
    font-size: 0.75rem;
    line-height: 1rem;
  }

  &.size-sm {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  &.size-base {
    font-size: 1rem;
    line-height: 1.5rem;
  }

  &.size-lg {
    font-size: 1.125rem;
    line-height: 1.75rem;
  }

  &.weight-normal {
    font-weight: 400;
  }
  &.weight-medium {
    font-weight: 500;
  }
  &.weight-semibold {
    font-weight: 600;
  }
  &.weight-bold {
    font-weight: 700;
  }

  &.family-display {
    letter-spacing: -0.025em;
  }
`;

export const Text = styled.p`
  color: var(--foreground);
  font-family: var(--font-sans);

  &.size-xs {
    font-size: 0.75rem;
    line-height: 1rem;
  }

  &.size-sm {
    font-size: 0.875rem;
    line-height: 1.25rem;
  }

  &.size-base {
    font-size: 1rem;
    line-height: 1.5rem;
  }

  &.size-lg {
    font-size: 1.125rem;
    line-height: 1.75rem;
  }

  &.size-xl {
    font-size: 1.25rem;
    line-height: 1.75rem;
  }

  &.size-2xl {
    font-size: 1.5rem;
    line-height: 2rem;
  }

  &.weight-normal {
    font-weight: 400;
  }

  &.weight-medium {
    font-weight: 500;
  }

  &.weight-semibold {
    font-weight: 600;
  }

  &.weight-bold {
    font-weight: 700;
  }

  &.family-display {
    font-family: var(--font-sans);
  }
`;

export const Flex = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const Input = styled.input`
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  font-size: 16px;
  color: var(--text);
  background-color: var(--background);

  &:focus {
    outline: none;
    // box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
  }
`;

export const Button = styled.button<{
  secondary?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  transparent?: boolean;
}>`
  display: block;
  padding: 8px 16px;
  font-size: 16px;
  font-weight: 500;
  background: ${props => (props.secondary ? '#fff' : '#000')};
  color: ${props => (props.secondary ? '#000' : '#fff')};
  text-align: center;
  border-radius: 8px;
  text-decoration: none;
  border: ${props => (props.secondary ? '1px solid #E7E7E7' : 'none')};
  cursor: pointer;
  opacity: ${props => (props.disabled ? '0.5' : '1')};
  transition: all 0.2s ease;
  max-width: 180px;

  &:hover {
    background: ${props => (props.secondary ? '#f5f5f5' : '#333')};
  }

  ${props =>
    props.fullWidth &&
    css`
      width: 100%;
      max-width: 100%;
    `}

  ${props =>
    props.transparent &&
    css`
      background: transparent;
      color: var(--text);

      &:hover {
        background: transparent;
      }
    `}
`;

export const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text);
`;

export const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  max-width: 550px;
  // background: #f8f8f8;
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  border: 1px solid #e7e7e7;
`;

export const BorderLine = styled.div`
  display: flex;
  width: 100%;
  height: 1px;
  background-color: var(--border);
  margin: 1rem 0;
`;

export const PlanInfo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  background-color: var(--border);
  padding: 10px 12px;
  border-radius: 15px;
  align-items: center;
`;

export const PlanBadge = styled.div<{isPro: boolean}>`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => (props.isPro ? 'var(--dark-text)' : 'var(--text)')};
  background-color: ${props => (props.isPro ? 'var(--dark-panel)' : '#d7d7d7')};
  padding: 0.25rem 0.75rem;
  border-radius: 25px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 1rem;
  max-width: 26px;
`;

export const PlanInfoSubtitle = styled.h4`
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text);
  margin: 0;
`;
